import React from 'react';
import { mediumImpact } from '@/utils/capacitorUtils';
import * as scoreUtils from '@/utils/scoreUtils';
import { useTranslation } from 'react-i18next';
import GolfIcon from '../GolfIcon';

interface ScoreInputsProps {
  score: number | null;
  putts: number | null;
  par: number;
  onScoreChange: (newScore: number | null) => void;
  onPuttsChange: (newPutts: number | null) => void;
  onScoreInteraction: () => void;
  onPuttsInteraction: () => void;
}

const ScoreInputs = ({
  score,
  putts,
  par,
  onScoreChange,
  onPuttsChange,
  onScoreInteraction,
  onPuttsInteraction
}: ScoreInputsProps) => {

  const { t } = useTranslation();
  const increaseScore = () => {
    mediumImpact();
    onScoreInteraction();
    const newScore = score === null ? par + 1 : score + 1;
    onScoreChange(newScore);
  };

  const decreaseScore = () => {
    mediumImpact();
    onScoreInteraction();
    // If score hasn't been set yet, default to par, otherwise decrement if > 1
    if (score === null) {
      onScoreChange(par);
    } else if (score > 1) {
      onScoreChange(score - 1);
    }
  };

  const increasePutts = () => {
    mediumImpact();
    onPuttsInteraction();

    // If putts haven't been set yet, default to 2 (assuming first putt)
    if (putts === null) {
      onPuttsChange(2);
      // If score is null, initialize it to par
      if (score === null) {
        onScoreChange(par);
        onScoreInteraction();
      }
      return;
    }

    // Simply increment putts - the parent component will handle the golf rule
    const newPutts = putts! + 1;
    onPuttsChange(newPutts);
  };

  const decreasePutts = () => {
    mediumImpact();
    onPuttsInteraction();

    // Putts default to 2
    if (putts === null) {
      onPuttsChange(2);
      return;
    }

    if (putts > 0) onPuttsChange(putts - 1);
  };

  // Get score label and class
  const getScoreLabelText = () => {
    if (score === null) return '';
    return scoreUtils.getScoreLabel(score, par);
  };

  const getScoreClass = () => {
    if (score === null) return '';
    return scoreUtils.getScoreClass(score, par);
  };

  return (
    <div className="mb-4">
      <div className="flex flex-row justify-between items-center">
        <div className='flex w-full items-center justify-between'>
          <div className="text-sm font-medium">{t('score')}</div>
          <div className="flex items-center justify-end h-12">
            {score && score > 1 && (!putts || score > (putts + 1)) && (
              <button onClick={decreaseScore} className="h-12 w-12 border-2 border-r-0 border-solid flex items-center justify-center border-input rounded-l-full">
                <GolfIcon name="minus" className={score > 1 ? 'opacity-100' : 'opacity-30'} />
              </button>
            )}
            {score && (
              <div className={`text-3xl font-bold border-2 border-solid border-input h-12 flex items-center justify-center py-0 px-4`}>
                {score}
              </div>
            )}
            <button onClick={!score || score < 15 ? increaseScore : () => { }} className={`h-12 w-12 border-l-0 border-2 border-solid flex items-center justify-center border-input ${score !== null && score > 0 ? 'rounded-r-full' : 'rounded-full'}`}>
              <GolfIcon name="plus" />
            </button>
          </div>
        </div>
      </div>
      <div className="text-end mr-2">
        <span className={`font-semibold ${getScoreClass()} border-none`}>{getScoreLabelText()}</span>
      </div>

      <div className="flex flex-row justify-between items-center mt-4">
        <div className='flex w-full items-center justify-between'>
          <div className="text-sm font-medium">{t('putts')}</div>
          <div className="flex items-center justify-end h-12">
            {(putts !== null && putts > 0) && (
              <button onClick={decreasePutts} className="h-12 w-12 border-2 border-solid flex items-center justify-center border-input rounded-l-full">
                <GolfIcon name="minus" className={putts > 0 ? 'opacity-100' : 'opacity-30'} />
              </button>
            )}
            {putts !== null && (
              <div className={`text-3xl font-bold border-y-2 border-solid border-input h-12 flex items-center justify-center py-0 px-4 ${putts === 0 ? 'border-l-2' : ''}`}>
                {putts}
              </div>
            )}
            <button onClick={putts === null || putts < 10 ? increasePutts : () => { }} className={`h-12 w-12 border-2 border-solid flex items-center justify-center border-input ${putts !== null ? 'rounded-r-full' : 'rounded-full'}`}>
              <GolfIcon name="plus" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreInputs as React.FC<ScoreInputsProps>;
